import type React from 'react';

import type { PaginationProps } from '~/components/Pagination/Pagination';
import { Pagination } from '~/components/Pagination/Pagination';
import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { isEnvEnabled } from '~/lib/env';
import type { FilterEntity } from '~/modules/settings/components/FilterList/hooks/useFilterChips';

import { FilterList } from './FilterList/FilterList';
import { Toolbar } from './Toolbar';

interface ListControlsProps extends PaginationProps {
  entity: FilterEntity;
  placeholder: string;
  setPageNumber: (page: number) => void;
  onFilterClick?: () => void;
  toolbarRightAddon?: React.ReactNode;
  internalFilter?: React.ReactNode;
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
}

export function ListControls({
  page,
  total,
  perPage,
  entity,
  placeholder,
  setPageNumber,
  onFilterClick,
  toolbarRightAddon,
  internalFilter,
}: ListControlsProps) {
  const isCurrentUserExternal = useIsExternalUser();
  const isPaginationFeatureEnabled = isEnvEnabled(
    'VITE_CONTAINER_SETTINGS_PSSF_ENABLED',
    false,
  );

  return (
    <>
      {isPaginationFeatureEnabled ? (
        <>
          <Toolbar
            placeholder={placeholder}
            rightAddon={toolbarRightAddon}
            onFilterClick={onFilterClick}
            setPageNumber={setPageNumber}
          />
          <div className="flex items-center justify-start gap-sm">
            {!isCurrentUserExternal && internalFilter ? (
              <>
                <div className="flex-none">{internalFilter} </div>
                <span className="mx-sm inline-block h-lg w-px bg-emphasis-subtle" />
              </>
            ) : null}
            <div className="flex-1">
              {onFilterClick ? <FilterList entity={entity} /> : null}
            </div>
            <div className="flex-none">
              <Pagination
                page={page}
                total={total}
                perPage={perPage}
                entity={entity}
                setPageNumber={setPageNumber}
              />
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

ListControls.displayName = 'ListControls';
